import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Grid, List, Filter } from 'lucide-react';
import { setSortBy, setActiveFilters } from '../../../redux/actions/filterActions';
import { fetchProducts } from '../../../redux/actions/productActions';
import { useSearchParams } from 'react-router-dom'; // Añadir estos imports
import Filters from '../Filters';
import GridView from './GridView';
import ListView from './ListView';
import Pagination from './Pagination';

const ProductList = () => {
    const dispatch = useDispatch();
    const [searchParams, setSearchParams] = useSearchParams(); // Para manejar parámetros de URL
    const [hasLoadedFiltersFromURL, setHasLoadedFiltersFromURL] = useState(false);

    // Selectores de Redux
    const { allProducts, loading, error } = useSelector(state => state.products);
    const { activeFilters, sortBy } = useSelector(state => state.filters);

    // Estados locales
    const [viewMode, setViewMode] = useState('grid');
    const [currentPage, setCurrentPage] = useState(1);
    const [productsPerPage] = useState(20);
    const [showFilters, setShowFilters] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    // Verificar tamaño de pantalla
    useEffect(() => {
        const checkIfMobile = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', checkIfMobile);
        return () => window.removeEventListener('resize', checkIfMobile);
    }, []);

    // Cargar filtros desde la URL al iniciar
    useEffect(() => {
        const page = parseInt(searchParams.get('page')) || 1;
        const sort = searchParams.get('sort') || 'soldest';
        const search = searchParams.get('search') || '';
        const category = searchParams.get('category') ? searchParams.get('category').split(',') : [];
    
        let tags = [];
        const tagsParam = searchParams.get('tags');
        if (tagsParam) {
            try {
                tags = JSON.parse(decodeURIComponent(tagsParam));
            } catch (e) {
                console.error("Error parsing tags from URL", e);
            }
        }
    
        dispatch(setSortBy(sort));
        dispatch(setActiveFilters({
            category,
            tags,
            search
        }));
        setCurrentPage(page);
        setHasLoadedFiltersFromURL(true); // ✅ ahora sí
    }, []);

    // Sincronizar cambios de filtros a la URL
    useEffect(() => {
        // Crear objeto con los parámetros
        const params = {};
        
        if (currentPage > 1) params.page = currentPage;
        if (sortBy !== 'soldest') params.sort = sortBy;
        if (activeFilters.search) params.search = activeFilters.search;
        
        if (activeFilters.category?.length > 0) {
            params.category = activeFilters.category.join(',');
        }
        
        if (activeFilters.tags?.length > 0) {
            params.tags = encodeURIComponent(JSON.stringify(activeFilters.tags));
        }
        
        // Actualizar la URL con los parámetros
        setSearchParams(params);
    }, [activeFilters, sortBy, currentPage, setSearchParams]);

    // Fetch productos cuando cambian filtros, sort o página
    useEffect(() => {
        if (!hasLoadedFiltersFromURL) return;
    
        dispatch(fetchProducts({
            page: currentPage,
            limit: productsPerPage,
            categoryIds: activeFilters.category,
            tagIds: activeFilters.tags,
            name: activeFilters.search,
            sortBy
        }));
    }, [dispatch, activeFilters, sortBy, currentPage, productsPerPage, hasLoadedFiltersFromURL]);

    const prevFilters = useRef(activeFilters);
    const prevSortBy = useRef(sortBy);

    // Resetear página al cambiar filtros
    useEffect(() => {
        const filtersChanged = JSON.stringify(prevFilters.current) !== JSON.stringify(activeFilters);
        const sortChanged = prevSortBy.current !== sortBy;
    
        if (filtersChanged || sortChanged) {
            setCurrentPage(1);
            setShowFilters(false);
        }
    
        // Actualizamos referencias para la próxima comparación
        prevFilters.current = activeFilters;
        prevSortBy.current = sortBy;
    }, [activeFilters, sortBy]);
    

    // Handlers
    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo(0, 0);
    };

    const handleSortChange = (option) => {
        dispatch(setSortBy(option));
        setCurrentPage(1);
    };

    // Productos y total
    const currentProducts = allProducts.products || [];
    const totalProducts = allProducts.totalItems || 0;
    
    // Render cargando
    if (loading) {
        return (
            <div className="flex flex-col lg:flex-row gap-8">
                <aside className="lg:w-64 flex-shrink-0">
                    <Filters />
                </aside>
                <main className="flex-1">
                    <div className="text-center py-12">
                        <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-gray-200 border-t-blue-600" />
                    </div>
                </main>
            </div>
        );
    }

    // Render error
    if (error) {
        return (
            <div className="flex flex-col lg:flex-row gap-8">
                <aside className="lg:w-64 flex-shrink-0">
                    <Filters />
                </aside>
                <main className="flex-1">
                    <div className="text-center py-12 text-red-600">
                        {error}
                    </div>
                </main>
            </div>
        );
    }
    // Render final
    return (
        <div className="flex flex-col lg:flex-row gap-8">
            {/* Filtros */}
            <aside className={`lg:w-80 flex-shrink-0 ${isMobile && !showFilters ? 'hidden' : ''}`}>
                <Filters showFilters={showFilters} setShowFilters={setShowFilters} />
            </aside>

            {/* Productos */}
            <main className="flex-1">
                <div className="flex justify-between items-center mb-6">
                    <div className="text-sm text-gray-500">
                        {totalProducts} productos encontrados
                    </div>

                    <div className="flex items-center gap-4">
                        {/* Botón de filtros en móvil */}
                        {isMobile && (
                            <button 
                                onClick={() => setShowFilters(!showFilters)} 
                                className="border rounded-lg px-3 py-2 flex items-center gap-2"
                            >
                                <Filter className="h-5 w-5" />
                                Filtros
                            </button>
                        )}

                        {/* Orden */}
                        <select
                            value={sortBy}
                            onChange={(e) => handleSortChange(e.target.value)}
                            className="border rounded-lg px-3 py-2 text-sm"
                        >
                            <option value="soldest">Populares</option>
                            <option value="newest">Nuevas</option>
                            <option value="name-asc">Nombre (A-Z)</option>
                            <option value="name-desc">Nombre (Z-A)</option>
                            <option value="price-low">Menor Precio</option>
                            <option value="price-high">Mayor Precio</option>
                        </select>

                        {/* Vista grid/list - Ocultar en móvil */}
                        {!isMobile && (
                            <div className="flex items-center gap-2 border rounded-lg p-1">
                                <button
                                    onClick={() => setViewMode('grid')}
                                    className={`p-1 rounded ${viewMode === 'grid' ? 'bg-gray-100' : ''}`}
                                >
                                    <Grid className="h-5 w-5" />
                                </button>
                                <button
                                    onClick={() => setViewMode('list')}
                                    className={`p-1 rounded ${viewMode === 'list' ? 'bg-gray-100' : ''}`}
                                >
                                    <List className="h-5 w-5" />
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Lista de productos */}
                {!currentProducts.length ? (
                    <div className="text-center py-12 text-gray-500">
                        No se encontraron productos que coincidan con los filtros seleccionados
                    </div>
                ) : (
                    <>
                        {viewMode === 'grid' ? (
                            <GridView products={currentProducts} />
                        ) : (
                            <ListView products={currentProducts} />
                        )}

                        {/* Paginación */}
                        {totalProducts > productsPerPage && (
                            <Pagination
                                currentPage={currentPage}
                                totalProducts={totalProducts}
                                productsPerPage={productsPerPage}
                                onPageChange={handlePageChange}
                            />
                        )}
                    </>
                )}
            </main>
        </div>
    );
};

export default ProductList;